import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SiteMobileHeader from '../components/SiteMobileHeader';
import StickyNavigation from '../components/StickyNavigation';
import HomeContactFooter from '../components/HomeContactFooter';
import MailingListModal from '../components/MailingListModal';
import ProjectLocationMap from '../components/ProjectLocationMap';
import homeHeroLogo from '../assets/Group12.svg';
import { shot2Image, elmoraMobileShowcaseImage as elmoraHeroMobileImage } from '../cdn/landingMedia';
import elmoraCardLogo from '../assets/project/elmora.svg';
import cardScrollIcon from '../assets/project/SVG.svg';
import {
  elmoraBrochurePdf,
  meetsIllustration,
  visualBuildingFacade,
  visualBusinessLounge,
  visualInfinityPool,
  visualOneBedBedroom,
  visualTwoBedBedroom,
} from '../cdn/elmoraMedia';
import amenityJoggingIcon from '../assets/project/jogging.svg';
import amenityGymIcon from '../assets/project/Clip path group.svg';
import amenityInfinityPoolIcon from '../assets/project/infiPool.svg';
import amenityBenchIcon from '../assets/project/bench.svg';
import amenityLobbyIcon from '../assets/project/lobby.svg';
import amenitySaunaIcon from '../assets/project/sauna.svg';
import { clearContactState, submitMailingListForm } from '../store/slices/contactSlice';
import { validateMailingListForm } from '../utils/mailingListValidation';
import { showToast } from '../utils/toast';
import { downloadAsset } from '../utils/downloadAsset';
import useReplayOnScrollReveal from '../hooks/useReplayOnScrollReveal';
import useStaggeredRiseOnView, { staggerLineClass } from '../hooks/useStaggeredRiseOnView';
import './LandingPage.css';
import './EiraPage.css';
import '../styles/SitePageShell.css';

const MAIL_MODAL_CLOSE_MS = 620;
const ELMORA_MAP_EMBED_URL =
  'https://maps.google.com/maps?q=Elmora+by+Neoterra+Developments,+Jumeirah+Garden+City,+Dubai&hl=en&z=16&output=embed';
const ELMORA_MAP_CENTER = { lat: 25.21435, lng: 55.26815 };
const AMENITY_ICON_SIZE = 58;

const ELMORA_AMENITIES = [
  { icon: amenityJoggingIcon, label: 'jogging track', iconArtboard: 54 },
  { icon: amenityGymIcon, label: 'Gymnasium', iconArtboard: 59 },
  { icon: amenityInfinityPoolIcon, label: 'Infinity Lap Pool', iconArtboard: 56 },
  {
    icon: amenityBenchIcon,
    label: 'Gardens with Serene Seating Areas',
    iconArtboard: 56,
    desktopLines: ['Gardens with Serene', 'Seating Areas'],
  },
  { icon: amenityLobbyIcon, label: 'Business Lounge', iconArtboard: 64 },
  { icon: amenitySaunaIcon, label: 'Sauna Suites', iconArtboard: 59 },
];

const ELMORA_MAP_TABS = ['Schools', 'Shop', 'Dine', 'Grocery', 'Attractions'];

const ELMORA_VISUAL_SLIDES = [
  visualInfinityPool,
  visualOneBedBedroom,
  visualTwoBedBedroom,
  visualBuildingFacade,
  visualBusinessLounge,
];
const ELMORA_VISUAL_SLIDES_MOBILE = ELMORA_VISUAL_SLIDES;
const ELMORA_VISUAL_AUTOPLAY_MS = 4500;

const ELMORA_OVERVIEW_SECOND_PARA_INDEX = 5;

const ELMORA_OVERVIEW = {
  titleLines: ['Where elegance', 'meets everyday living'],
  mobileParagraphs: [
    "Nestled within one of Dubai's most promising residential destinations, ELMORA is a thoughtfully envisioned community where contemporary design, natural serenity, and refined living come together effortlessly.",
    'Offering an exclusive collection of Studios, 1, 2 & 3-Bedroom residences, every home is crafted with spacious layouts, premium finishes, and expansive windows that welcome natural light while showcasing beautifully landscaped surroundings. Every detail has been carefully considered to create a home that is both inspiring and enduring.',
  ],
  desktopBodyLines: [
    "Nestled within one of Dubai's most promising",
    'residential destinations, ELMORA is a thoughtfully',
    'envisioned community where contemporary',
    'design, natural serenity, and refined living come',
    'together effortlessly.',
    'Offering an exclusive collection of Studios, 1, 2 &',
    '3-Bedroom residences, every home is crafted',
    'with spacious layouts, premium finishes, and',
    'expansive windows that welcome natural light',
    'while showcasing beautifully landscaped',
    'surroundings. Every detail has been carefully',
    'considered to create a home that is both',
    'inspiring and enduring.',
  ],
};

const ELMORA_OVERVIEW_BODY_LINE_CLASS =
  "block font-['Poppins',sans-serif] text-[16px] font-light leading-[26px] text-[#0A0A0AC7] lg:text-[19.7px] lg:leading-[30px]";

const ELMORA_OVERVIEW_CTA_CLASS =
  "inline-flex h-[43px] w-[240px] min-w-[240px] max-w-full items-center justify-center whitespace-nowrap rounded-[25px] border border-solid border-black px-[27.7px] font-['Inter',sans-serif] text-[12px] font-normal uppercase leading-[18px] tracking-[2.5px] text-black transition-colors duration-200 hover:bg-black hover:text-white";

const getVisualDposClass = (offset) => {
  if (offset === 0) return 'eira-visual-dpos--center';
  if (offset === -1) return 'eira-visual-dpos--left';
  if (offset === 1) return 'eira-visual-dpos--right';
  if (offset === -2) return 'eira-visual-dpos--far-left';
  if (offset === 2) return 'eira-visual-dpos--far-right';
  return offset < 0 ? 'eira-visual-dpos--hidden-left' : 'eira-visual-dpos--hidden-right';
};
const ELMORA_VISUAL_COUNT = ELMORA_VISUAL_SLIDES.length;
const ELMORA_VISUAL_PAIRS = ELMORA_VISUAL_SLIDES.map((d, i) => ({ d, m: ELMORA_VISUAL_SLIDES_MOBILE[i] }));
// Clone last slide before + first two slides after for seamless infinite loop.
const ELMORA_VISUAL_EXTENDED = [
  ELMORA_VISUAL_PAIRS[ELMORA_VISUAL_COUNT - 1],
  ...ELMORA_VISUAL_PAIRS,
  ELMORA_VISUAL_PAIRS[0],
  ELMORA_VISUAL_PAIRS[1],
];

const ElmoraPage = () => {
  const dispatch = useDispatch();
  const contactSubmitting = useSelector((state) => state.contact.loading);

  const [mailOpen, setMailOpen] = useState(false);
  const [mailClosing, setMailClosing] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [promotionalConsent, setPromotionalConsent] = useState(false);
  const [activeMapTab, setActiveMapTab] = useState('Schools');
  // Track position runs over the extended (cloned) list; index 1 = first real slide.
  const [visualPos, setVisualPos] = useState(1);
  const [visualAnimate, setVisualAnimate] = useState(true);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 402));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useReplayOnScrollReveal('.project-reveal-on-scroll', 'is-visible', 0.12, '80px 0px -10% 0px', false);

  const overviewRevealRef = useRef(null);

  useStaggeredRiseOnView(overviewRevealRef, {
    individual: true,
    durationMs: 1200,
    distancePx: 28,
    threshold: 0.14,
    rootMargin: '0px 0px -8% 0px',
    replay: true,
    reverseOnLeave: true,
    desktopOnly: false,
  });

  const visualPosRef = useRef(visualPos);

  useEffect(() => {
    visualPosRef.current = visualPos;
  }, [visualPos]);

  const advanceVisualSlide = useCallback((direction) => {
    setVisualAnimate(true);
    setVisualPos((p) => Math.max(0, Math.min(ELMORA_VISUAL_COUNT + 1, p + direction)));
  }, []);

  const finishVisualSlide = useCallback((pos) => {
    if (pos >= ELMORA_VISUAL_COUNT + 1) {
      setVisualAnimate(false);
      setVisualPos(1);
    } else if (pos <= 0) {
      setVisualAnimate(false);
      setVisualPos(ELMORA_VISUAL_COUNT);
    }
  }, []);

  const goVisualSlide = useCallback((direction) => {
    advanceVisualSlide(direction);
  }, [advanceVisualSlide]);

  const handleDeskTransitionEnd = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== 'left') return;
    finishVisualSlide(visualPosRef.current);
  }, [finishVisualSlide]);

  const handleMobileTransitionEnd = useCallback((e) => {
    if (e?.propertyName !== 'transform') return;
    finishVisualSlide(visualPosRef.current);
  }, [finishVisualSlide]);

  useEffect(() => {
    const id = window.setInterval(() => advanceVisualSlide(1), ELMORA_VISUAL_AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [advanceVisualSlide]);

  // Re-enable the transition on the next frame after a no-transition loop reset.
  useEffect(() => {
    if (visualAnimate) return undefined;
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setVisualAnimate(true)));
    return () => cancelAnimationFrame(raf);
  }, [visualAnimate]);

  useEffect(() => {
    document.title = 'Elmora | Neoterra Developments';
  }, []);

  const handleDownloadBrochure = useCallback(() => {
    downloadAsset(elmoraBrochurePdf, 'Elmora Brochure.pdf');
  }, []);

  const openMailModal = useCallback(() => {
    setMailClosing(false);
    setMailOpen(true);
  }, []);

  const closeMailModal = useCallback(() => {
    if (mailClosing || !mailOpen) return;
    setMailClosing(true);
    window.setTimeout(() => {
      setMailOpen(false);
      setMailClosing(false);
    }, MAIL_MODAL_CLOSE_MS);
  }, [mailClosing, mailOpen]);

  const handleMailSubmit = async (e) => {
    e.preventDefault();
    const validation = validateMailingListForm({ fullName, email, phone });

    if (!validation.isValid) {
      showToast(validation.error, 'error');
      return;
    }

    const { name, email: emailValue, contactNumber } = validation.values;

    try {
      await dispatch(
        submitMailingListForm({
          name,
          email: emailValue,
          contactNumber,
          promotionalConsent,
          sourcePage: 'elmora',
        }),
      ).unwrap();
      showToast('Thanks! We will keep you informed.', 'success');
      setFullName('');
      setEmail('');
      setPhone('');
      setPromotionalConsent(false);
      closeMailModal();
    } catch (err) {
      showToast(typeof err === 'string' ? err : 'Something went wrong. Please try again.', 'error');
    } finally {
      dispatch(clearContactState());
    }
  };

  return (
    <div className="landing-page eira-page elmora-page site-page-shell">
      <SiteMobileHeader
        variant={viewportWidth >= 1024 ? 'homeHero' : undefined}
        logoSrc={viewportWidth >= 1024 ? homeHeroLogo : undefined}
        barClassName="home-top-bar"
        onEnquireClick={openMailModal}
      />

      <div className="relative w-full">
        <section className="eira-page-hero relative h-[100vh] min-h-[600px] w-full overflow-hidden" aria-label="Elmora hero">
          <picture className="eira-page-hero__image absolute inset-0 z-[1] block h-full w-full">
            <source media="(min-width: 1024px)" srcSet={shot2Image} />
            <img
              src={elmoraHeroMobileImage}
              alt=""
              className="absolute inset-0 block h-full w-full object-cover object-center"
              fetchPriority="high"
              decoding="async"
            />
          </picture>
        </section>

        <div className="eira-hero-card project-reveal-on-scroll z-[10] flex h-[243px] w-[240px] max-w-[240px] items-center justify-center rounded-[7px] border border-solid border-[#DDDDDD] bg-white px-[30px] pb-[50px] pt-[50px] opacity-100 shadow-[0_14px_18px_-14px_rgba(0,0,0,0.45)]">
          <img
            src={elmoraCardLogo}
            alt="Elmora"
            width={178}
            height={141}
            className="block h-[110px] w-[104px] max-w-none object-contain opacity-100 lg:h-[141px] lg:w-[178px]"
            loading="eager"
          />
        </div>

        <img
          src={cardScrollIcon}
          alt=""
          width={20}
          height={20}
          className="eira-scroll-icon project-reveal-on-scroll project-reveal-on-scroll--delay-1 z-[10] block object-contain opacity-100"
          aria-hidden="true"
        />
      </div>

      <section
        className="eira-overview relative flex flex-col items-center bg-[#FCFBF7] py-[48px] max-lg:!mt-[40px] lg:h-[713px] lg:flex-row lg:py-0"
        aria-label="Elmora overview"
      >
        <div
          ref={overviewRevealRef}
          className="w-full max-w-full px-[24px] text-center lg:w-[48%] lg:max-w-[620px] lg:pl-[clamp(48px,7vw,120px)] lg:pr-[clamp(8px,1vw,16px)] lg:text-left"
        >
          <h2 className="eira-section-heading eira-section-heading--overview">
            {ELMORA_OVERVIEW.titleLines.map((line, index) => (
              <span
                key={line}
                className={staggerLineClass}
                data-content-line
                data-reveal-delay={String(index * 120)}
              >
                {line}
              </span>
            ))}
          </h2>

          <div className="mt-[20px] lg:hidden">
            {ELMORA_OVERVIEW.mobileParagraphs.map((paragraph, index) => (
              <p
                key={paragraph.slice(0, 32)}
                className={`${ELMORA_OVERVIEW_BODY_LINE_CLASS} ${index > 0 ? 'mt-[16px]' : ''} ${staggerLineClass}`}
                data-content-line
                data-reveal-delay={String((ELMORA_OVERVIEW.titleLines.length + index) * 120)}
              >
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-[28px] hidden lg:block">
            {ELMORA_OVERVIEW.desktopBodyLines.map((line, index) => (
              <span
                key={line}
                className={`${ELMORA_OVERVIEW_BODY_LINE_CLASS} ${
                  index === ELMORA_OVERVIEW_SECOND_PARA_INDEX ? 'mt-[20px]' : ''
                } ${staggerLineClass}`}
                data-content-line
                data-reveal-delay={String((ELMORA_OVERVIEW.titleLines.length + index) * 120)}
              >
                {line}
              </span>
            ))}
          </div>

          <button
            type="button"
            className={`mt-[32px] ${ELMORA_OVERVIEW_CTA_CLASS} lg:hidden ${staggerLineClass}`}
            data-content-line
            data-reveal-delay={String((ELMORA_OVERVIEW.titleLines.length + ELMORA_OVERVIEW.mobileParagraphs.length) * 120)}
            onClick={handleDownloadBrochure}
          >
            Download Brochure
          </button>
          <button
            type="button"
            className={`mt-[40px] hidden ${ELMORA_OVERVIEW_CTA_CLASS} lg:inline-flex ${staggerLineClass}`}
            data-content-line
            data-reveal-delay={String((ELMORA_OVERVIEW.titleLines.length + ELMORA_OVERVIEW.desktopBodyLines.length) * 120)}
            onClick={handleDownloadBrochure}
          >
            Download Brochure
          </button>
        </div>

        <div className="project-reveal-on-scroll project-reveal-on-scroll--overview-art static mx-auto mt-[36px] lg:absolute lg:right-[clamp(60px,10vw,200px)] lg:top-1/2 lg:mx-0 lg:mt-0 lg:-translate-y-1/2">
          <img
            src={meetsIllustration}
            alt=""
            className="block mx-auto h-auto w-[330px] max-w-[90%] object-contain lg:h-[clamp(333px,36.81vw,530px)] lg:w-[clamp(400px,44.17vw,636px)] lg:max-w-none"
            aria-hidden="true"
          />
        </div>
      </section>

      <section className="w-full bg-white py-[clamp(32px,6vh,80px)]" aria-label="A visual symphony">
        <h2 className="eira-section-heading eira-section-heading--visual project-reveal-on-scroll px-4">
          A Visual Symphony
        </h2>

        <div className="project-reveal-on-scroll project-reveal-on-scroll--delay-1 relative mt-[clamp(20px,4vh,48px)] w-full overflow-hidden">
          <div className="relative flex w-full items-center justify-center lg:hidden">
            <div className="relative z-[2] h-[483px] w-[1005px] max-w-[88%] overflow-hidden rounded-[10px]">
              <div
                className={`flex h-full w-full ${visualAnimate ? 'transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]' : ''}`}
                style={{ transform: `translateX(-${visualPos * 100}%)` }}
                onTransitionEnd={handleMobileTransitionEnd}
              >
                {ELMORA_VISUAL_EXTENDED.map((pair, idx) => (
                  <div key={idx} className="relative h-full w-full shrink-0 basis-full">
                    <img
                      src={pair.m}
                      alt=""
                      aria-hidden="true"
                      className="h-full w-full object-cover"
                      loading={idx <= 1 ? 'eager' : 'lazy'}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className={`eira-visual-desk-carousel mx-auto hidden w-full lg:block${
              visualAnimate ? '' : ' eira-visual-desk-carousel--no-transition'
            }`}
          >
            {ELMORA_VISUAL_EXTENDED.map((pair, idx) => {
              const offset = idx - visualPos;
              const posClass = getVisualDposClass(offset);
              const isSide = Math.abs(offset) === 1;
              const handleSideClick = offset === -1
                ? () => goVisualSlide(-1)
                : offset === 1
                  ? () => goVisualSlide(1)
                  : undefined;

              return (
                <div
                  key={`elmora-visual-dpos-${idx}`}
                  className={`eira-visual-dpos ${posClass}`}
                  onClick={handleSideClick}
                  onKeyDown={isSide ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSideClick?.();
                    }
                  } : undefined}
                  onTransitionEnd={offset === 0 ? handleDeskTransitionEnd : undefined}
                  role={isSide ? 'button' : undefined}
                  tabIndex={isSide ? 0 : undefined}
                  aria-label={offset === -1 ? 'Previous image' : offset === 1 ? 'Next image' : undefined}
                  aria-hidden={offset < -1 || offset > 1}
                >
                  <img
                    src={pair.d}
                    alt=""
                    aria-hidden="true"
                    className="eira-visual-dpos__img block h-full w-full object-cover object-center"
                    decoding="async"
                    loading="eager"
                  />
                </div>
              );
            })}
            {visualPos === 0 && (
              <div
                className="eira-visual-dpos eira-visual-dpos--left"
                onClick={() => goVisualSlide(-1)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goVisualSlide(-1);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Previous image"
              >
                <img
                  src={ELMORA_VISUAL_PAIRS[ELMORA_VISUAL_COUNT - 2].d}
                  alt=""
                  aria-hidden="true"
                  className="eira-visual-dpos__img block h-full w-full object-cover object-center"
                  decoding="async"
                  loading="eager"
                />
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => goVisualSlide(-1)}
            className="absolute left-[clamp(20px,3vw,52px)] top-1/2 z-[4] hidden -translate-y-1/2 items-center justify-center border-0 bg-transparent p-2 lg:inline-flex"
            aria-label="Previous image"
          >
            <svg
              width="17.0975"
              height="34.1944"
              viewBox="0 0 17.0975 34.1944"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M15.36 1.55 L2.15 17.1 L15.36 32.64" stroke="#FFFFFF" strokeWidth="2.08" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <button
            type="button"
            onClick={() => goVisualSlide(1)}
            className="absolute right-[clamp(20px,3vw,52px)] top-1/2 z-[4] hidden -translate-y-1/2 items-center justify-center border-0 bg-transparent p-2 lg:inline-flex"
            aria-label="Next image"
          >
            <svg
              width="17.0975"
              height="34.1944"
              viewBox="0 0 17.0975 34.1944"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M1.74 1.55 L14.95 17.1 L1.74 32.64" stroke="#FFFFFF" strokeWidth="2.08" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </section>

      <section className="w-full bg-white py-[clamp(32px,6vh,80px)]" aria-label="Designed for every passion">
        <h2 className="eira-section-heading eira-section-heading--passion project-reveal-on-scroll px-[6px] lg:px-4">
          <span className="block lg:hidden">Designed for Every</span>
          <span className="block lg:hidden">Passion</span>
          <span className="hidden lg:inline">Designed for Every Passion</span>
        </h2>

        <div className="mx-auto mt-[clamp(24px,4vh,48px)] flex max-w-[1296px] flex-wrap items-center justify-center gap-[26px] px-[3%] lg:gap-[clamp(10px,1.4vw,24px)] lg:px-[clamp(16px,4vw,72px)]">
          {ELMORA_AMENITIES.map(({ icon, label, iconArtboard, desktopLines }, index) => (
            <div
              key={label}
              className="project-reveal-on-scroll flex h-[180px] w-[42%] max-w-[177.5px] flex-col items-center justify-center gap-[15px] rounded-[7px] bg-[#FCFBF7] px-[15px] py-[30px] text-center sm:w-[160px] lg:h-[200px] lg:w-[177.5px]"
              style={{ transitionDelay: `${120 + index * 90}ms` }}
            >
              <span className="eira-amenity-icon-shell" aria-hidden="true">
                <img
                  src={icon}
                  alt=""
                  className="eira-amenity-icon"
                  style={{ transform: `scale(${AMENITY_ICON_SIZE / iconArtboard})` }}
                  loading="lazy"
                />
              </span>
              <span className="font-['Poppins',sans-serif] text-[14px] font-light uppercase leading-[19px] tracking-[0] text-[#000000] lg:font-normal lg:text-[#0A0A0AC7]">
                {desktopLines ? (
                  <>
                    <span className="lg:hidden">{label}</span>
                    <span className="hidden lg:block">
                      <span className="block whitespace-nowrap">{desktopLines[0]}</span>
                      {desktopLines[1]}
                    </span>
                  </>
                ) : (
                  label
                )}
              </span>
            </div>
          ))}
        </div>

        <div className="project-reveal-on-scroll project-reveal-on-scroll--delay-2 mt-[clamp(40px,8vh,96px)] flex flex-nowrap items-center justify-center gap-[5px] px-[12px] lg:mt-[clamp(56px,8vh,96px)] lg:flex-wrap lg:gap-[10px] lg:px-0">
          {ELMORA_MAP_TABS.map((tab) => {
            const isActive = tab === activeMapTab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveMapTab(tab)}
                className={`inline-flex h-[30px] min-w-0 items-center justify-center whitespace-nowrap rounded-[5px] border border-solid px-[8px] font-['Poppins',sans-serif] text-[10px] font-normal transition-colors duration-200 lg:h-[38px] lg:min-w-[72px] lg:rounded-[5px] lg:px-[18px] lg:text-[15px] ${
                  isActive
                    ? 'border-transparent bg-[#BC8639] text-white'
                    : 'border-[#0C192D] bg-white text-[#0A0A0AC7] hover:border-[#BC8639]'
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        <div className="project-reveal-on-scroll project-reveal-on-scroll--delay-3">
          <ProjectLocationMap
            project="elmora"
            projectName="Elmora"
            category={activeMapTab}
            center={ELMORA_MAP_CENTER}
            fallbackEmbedUrl={ELMORA_MAP_EMBED_URL}
            projectAreaLabel="Elmora · Jumeirah Garden City, Dubai"
            title="Elmora location map"
          />
        </div>
      </section>

      <StickyNavigation wrapperClassName="home-navigation-sticky" onInquireClick={openMailModal} />

      <div className={`site-page-footer-wrap project-reveal-on-scroll relative z-[30] isolate ${viewportWidth >= 1024 ? 'bg-[#162D24]' : 'bg-white'}`}>
        <HomeContactFooter
          variant={viewportWidth >= 1024 ? 'home' : 'default'}
          sectionId="elmora-contact"
        />
      </div>

      <MailingListModal
        open={mailOpen}
        closing={mailClosing}
        fullName={fullName}
        email={email}
        phone={phone}
        promotionalConsent={promotionalConsent}
        submitting={contactSubmitting}
        onClose={closeMailModal}
        onSubmit={handleMailSubmit}
        onFullNameChange={setFullName}
        onEmailChange={setEmail}
        onPhoneChange={setPhone}
        onPromotionalConsentChange={setPromotionalConsent}
      />
    </div>
  );
};

export default ElmoraPage;
